import type { ReportId } from './reportsView';

// Rediseño · Bloque F — las tres pantallas de Reportes y cómo se va de una a otra, sin reestructurar el
// router (como `newProductRoute.ts` en el Bloque E).
//
// · `#owner-dashboard` es la sección de siempre (el id se conserva: no rompe ningún enlace guardado) y
//   ahora abre la pantalla de las siete preguntas (P-030). Cada reporte tiene su ruta: `#owner-dashboard/negocio`
//   (R1), `/dinero` (R2) y, desde P-084, `/productos` (R3), `/horarios` (R4), `/costos` (R5), `/inventario` (R6) y
//   `/operacion` (R7). El router solo mira el primer segmento: las ocho son la misma sección y el menú resalta «Reportes».
// · Un segundo segmento que no es ningún reporte se consume: la URL vuelve a `#owner-dashboard` sin
//   navegar, y se ve la pantalla de las preguntas. La URL no dice algo que la pantalla no es (como N8).
// · «‹ Reportes» vuelve a las preguntas. Si el reporte se abrió desde ellas, con el Atrás del historial
//   (así el Atrás siguiente no lo reabre); si se llegó de otro lado —el Inicio, un enlace, una recarga—,
//   reemplazando la entrada, sin lazo de Atrás. La marca viaja en `history.state` y sobrevive a una recarga.

export const REPORTS_SECTION = 'owner-dashboard';
export type ReportsScreen = 'landing' | ReportId;

const REPORT_IDS: readonly ReportId[] = ['negocio', 'dinero', 'productos', 'horarios', 'costos', 'inventario', 'operacion'];

/** La ruta de un reporte: `owner-dashboard/negocio`. */
export function reportHash(id: ReportId): string {
  return `${REPORTS_SECTION}/${id}`;
}

/**
 * Qué pantalla pide un hash de Reportes; `unknown` si trae un segundo segmento que no es nada. Un hash de
 * OTRA sección no es de Reportes y no se toca: al irse de Reportes a `#inventory/movements`, la página
 * todavía oye ese cambio antes de desmontarse, y no puede tomarlo por un reporte desconocido.
 */
export function reportsScreenFromHash(hash: string): { screen: ReportsScreen; unknown: boolean } {
  const [first, second] = hash.replace('#', '').split('/');
  if (first !== REPORTS_SECTION || !second) return { screen: 'landing', unknown: false };
  const id = REPORT_IDS.find((candidate) => candidate === second);
  return id ? { screen: id, unknown: false } : { screen: 'landing', unknown: true };
}

/**
 * La pantalla de la URL actual. Si su segundo segmento no es nada, se consume: la URL vuelve a
 * `#owner-dashboard` sin evento de cambio de hash (no es una navegación).
 */
export function currentReportsScreen(): ReportsScreen {
  const { screen, unknown } = reportsScreenFromHash(window.location.hash);
  if (unknown) window.history.replaceState(window.history.state, '', `#${REPORTS_SECTION}`);
  return screen;
}

const ENTRY_KEY = 'n360Report';

function readEntry(): 'landing' | undefined {
  const state: unknown = window.history.state;
  if (state && typeof state === 'object' && (state as Record<string, unknown>)[ENTRY_KEY] === 'landing') return 'landing';
  return undefined;
}

/** Desde las preguntas: se abre el reporte y su entrada del historial recuerda que detrás están ellas. */
export function openReportFromLanding(id: ReportId): void {
  window.location.hash = reportHash(id);
  const state: unknown = window.history.state;
  window.history.replaceState({ ...(state && typeof state === 'object' ? state : {}), [ENTRY_KEY]: 'landing' }, '');
}

/** «‹ Reportes»: a las preguntas, sin dejar un Atrás que reabra el reporte. */
export function leaveReport(): void {
  if (readEntry() === 'landing') window.history.back();
  else window.location.replace(`#${REPORTS_SECTION}`);
}
